import { Box, Button, CircularProgress, ClickAwayListener, Dialog, Divider, Fab, FormControl, FormHelperText, IconButton, ListSubheader, MenuItem, MenuList, Popover, Popper, Select, TextField, Typography, useMediaQuery, useTheme } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { ErrorMessage, Field, Form, Formik } from 'formik'
import { observer } from 'mobx-react'
import * as Yup from 'yup'
import dayjs from 'dayjs'

import buttonWaveSimba from '../../../assets/simba-ai/buttonwavesimba.png'
import DataLoadingSpinner from '../../common/DataLoadingSpinner'
import CustomSelectField from '../../common/CustomSelectField'
import { useSimbaAIStore } from '../../../store/simbaai.store'
import { formatDate, Icons } from '../../../utils/utilities'
import { useUserStore } from '../../../store/user.store'
import CustomChip from '../../common/CustomChip'
import { useNavigate } from 'react-router-dom'
import api from '../../../service/api'
import { toast } from 'react-toastify'
import { useSettingsStore } from '../../../store/settings.store'


const AddModuleButton = ( { label = "Add Module", onselect, modules } ) => {

    const { palette } = useTheme()

    const handleClickOnModule = ( module ) => {
        onselect( module )
    }

    return (
        <CustomSelectField value="" id="moduleAdder" placeholder="Module" size="small" onChange={event => handleClickOnModule( event.target.value )} data={modules} />
    )
}

const validationSchema = Yup.object().shape( {
    title: Yup.string().required( "Title field is required" ).max( 200, "Maximum characters: 200" ),
    modules_and_topics: Yup
        .object()
        .required( "Practice session must have atleast one module" )
        .test( "ATLEAST_ONE_MODULE", "Practice session must have atleast one module", ( value ) => {
            return Object.keys( value ).length > 0
        } )
        .test( "ATLEAST_ONE_TOPIC_IN_EACH_MODULE", "All modules should have atleast one topic.", ( value ) => {
            return Object.keys( value ).every( mod => value[mod].topics.length > 0 )
        } )
        .test( "MAX_TOPIC_IN_EACH_MODULE", "All modules should have no more than 3 topic.", ( value ) => {
            return Object.keys( value ).every( mod => value[mod].topics.length <= 3 )
        } )
        .test( "ALL_MODULE_HAVE_NAME", "All module should have valid names.", ( value ) => {
            return Object.keys( value ).every( mod => Boolean( value[mod].name?.trim() ) )
        } ),
    access_groups: Yup
        .mixed( "Access groups must be a array." )
        .required( "Atleast one batch or section must be specified." )
        .test( "ATLEAST_ONE_ACCESS_GROUPS", "Practice session must have atleast one bacth or section", ( value ) => {
            return value.length > 0
        } ),
    subject: Yup.mixed().required( "Subject details are required." ),
    date_range_start: Yup
        .mixed()
        .required( "Date range values are required." )
        .test( "START_LESS_THAN_END", "Date range start should be less than end date.", ( value, { parent: { date_range_end } } ) => {
            return value < date_range_end
        } )
        .test( "START_LESS_DIFF_BETWEEN_15_DAYS", "Practice session date range should not exceed 15 days.", ( value, { parent: { date_range_end } } ) => {
            return date_range_end.diff( value, 'day' ) <= 15
        } ),
    close_date: Yup.mixed().optional().nullable()
        .test( "MUST_BE_OPEN_FOR_5_DAYS", "The practice sessions must be open for atleast 5 days.", val => {
            if ( !val ) return true
            if ( val )
                return dayjs( val ).diff( dayjs() ) > 5
        } )
} )

const subjectFilterFunc = ( sub, values ) => {

    if ( sub.ac_year_id !== values.academicYear ) return false

    return true
}

const accessGroupsFilterFunc = ( item, values, filterEntity ) => {

    if ( filterEntity === "batch" ) {
        if ( item.entity !== 'batch' ) return false

        if ( !item.batch_assignment_id ) return false

        if ( +item.ac_year_id !== +values.academicYear ) return false

        if ( values.year && item.current_year !== values.year ) return false

        if ( values.sem && item.current_sem && values.sem !== item.current_sem ) return false

        return true
    } else {

        if ( item.entity !== 'section' ) return false

        if ( !item.section_assignment_id ) return false

        if ( +item.ac_year_id !== +values.academicYear ) return false

        if ( values.year && item.current_year !== values.year ) return false

        if ( item.current_sem && values.sem && values.sem !== item.current_sem ) return false

        return true
    }
}

const complexityOptions = [
    { value: "EASY", label: "Easy" },
    { value: "MEDIUM", label: "Medium" },
    { value: "HARD", label: "Hard" },
]

const CreateForm = observer( ( { practiceSession = null, mode = "create", open, setOpen } ) => {

    const [yearsAndSems, setYearsAndSems] = useState( { years: [], sems: {} } )
    const [uploadingAttachments, setUploadingAttachments] = useState( [] )
    const [fetchingSubjects, setFetchingSubjects] = useState( false )
    const [refreshing, setRefreshing] = useState( false )
    const [creating, setCreating] = useState( false )

    const { border, palette, layout, components, shadows, zIndex } = useTheme()

    const SettingsStore = useSettingsStore()
    const SimbaAIStore = useSimbaAIStore()
    const UserStore = useUserStore()

    const navigate = useNavigate()

    const initialValues = useMemo( () => (
        {
            title: mode === "edit" ? practiceSession.title : "",
            modules_and_topics: mode === "edit" ? practiceSession.modules_and_topics : {},
            module: "",
            year: "",
            sem: "",
            topics: mode === "edit" ? practiceSession.topics : [],
            topic: "",
            date_range_start: mode === "edit" ? dayjs( practiceSession.date_range_start ) : dayjs().subtract( 7, 'days' ),
            date_range_end: mode === "edit" ? dayjs( practiceSession.date_range_end ) : dayjs(),
            academicYear: mode === "edit" ? practiceSession.subject.ac_year_id : UserStore.getUserAcademicYears[0]?.ac_year_id,
            subject: "",
            access_groups: mode === "edit" ? practiceSession.access_groups : [],
            selected_access_groups: mode === "edit" ? practiceSession.access_groups?.map( ag => ag.id ) : [],
            reference_materials: mode === "edit" ? practiceSession.reference_materials?.map( rm => rm._id ) : [],
            close_date: null,
            complexity: 'EASY',
            subject: mode === "edit" ? {
                subject_id: practiceSession?.subject?.subject_id,
                subject_code: practiceSession?.subject?.subject_code,
                subject_name_short: practiceSession?.subject?.subject_name_short,
                subject_name: practiceSession?.subject?.subject_name,
                ac_year_id: practiceSession?.subject?.ac_year_id,
                ac_year: practiceSession?.subject?.ac_year,
                year: practiceSession?.subject?.year,
                sem: practiceSession?.subject?.sem,
            } : null,
        }
    ), [practiceSession, mode, UserStore] )

    const moduleOptions = Array.from( { length: ( SettingsStore?.getUseSettings['max_modules_per_subject'] || 5 ) }, ( _, i ) => `Module ${i + 1}` )

    const onClose = () => {
        setOpen( false )
    }

    const updateModuleName = ( moduleKey, name, values, setFieldValue ) => {
        setFieldValue( 'modules_and_topics', {
            ...values.modules_and_topics,
            [moduleKey]: {
                ...values.modules_and_topics[moduleKey],
                name
            }
        } )
    }

    const addTopic = ( moduleKey, topic, values, setFieldValue ) => {
        if ( !topic ) return toast( "Topic name is required to add topic." )

        const topics = new Set( values.modules_and_topics[moduleKey].topics )
        topics.add( topic )

        setFieldValue( 'modules_and_topics', {
            ...values.modules_and_topics,
            [moduleKey]: {
                ...values.modules_and_topics[moduleKey],
                topics: Array.from( topics )
            }
        } )
    }

    const removeTopic = ( moduleKey, topicIndex, values, setFieldValue ) => {
        const topics = [...values.modules_and_topics[moduleKey].topics]
        topics.splice( topicIndex, 1 )

        setFieldValue( 'modules_and_topics', {
            ...values.modules_and_topics,
            [moduleKey]: {
                ...values.modules_and_topics[moduleKey],
                topics
            }
        } )
    }

    const removeModule = ( moduleKey, values, setFieldValue ) => {
        const updated = { ...values.modules_and_topics }
        delete updated[moduleKey]
        setFieldValue( 'modules_and_topics', updated )
    }

    const getBatches = async ( year, refresh = false ) => {
        setRefreshing( "batches" )
        await UserStore.fetchUserBatchesAndSection( year, refresh )
        setRefreshing( null )
    }

    const refreshYears = async () => {
        setRefreshing( "years" )
        await UserStore.fetchUserAcademicYears( true )
        setRefreshing( null )
    }

    const refreshBatches = async ( academicYear ) => {
        if ( academicYear )
            await getBatches( academicYear, true )
    }

    const handleAccessGroupChange = ( event, setFieldValue ) => {
        const value = event.target.value
        setFieldValue( 'selected_access_groups', value )
        const accessGroups = value.map( sag => UserStore.getUsersAllBatchesAndSectionList.find( ag => ag.id === sag ) )
        setFieldValue( 'access_groups', accessGroups )
    }

    const removeSelectedAccessGroup = ( event, index, values, setFieldValue ) => {
        event.stopPropagation()
        const newSelectedIds = values.selected_access_groups || []
        const newSelectedGroups = values.access_groups || []
        newSelectedIds.splice( index, 1 )
        newSelectedGroups.splice( index, 1 )
        setFieldValue( 'access_groups', newSelectedIds )
        setFieldValue( 'access_groups', newSelectedGroups )
    }

    const handleNewModuleSelect = ( module, values, setFieldValue ) => {
        if ( !values.modules_and_topics[module] ) {
            const selectedSubjectPracticeSessions = SimbaAIStore.getPracticeSessions.find( ps => ps.subject?.subject_code === values.subject?.subject_code && Boolean( ps.modules_and_topics[module] ) )
            setFieldValue( 'modules_and_topics', {
                ...values.modules_and_topics,
                [module]: { name: selectedSubjectPracticeSessions?.modules_and_topics[module]?.name || "", topics: [] }
            } )
        }
    }

    const handleSubjectChange = async ( e, setValue, date ) => {
        const subjectDetails = UserStore.getUserSubjectsDetails?.currentSubjects.find( s => s?.subject_id === e.target.value )
        const subjectValue = {
            subject_id: subjectDetails.subject_id,
            subject_code: subjectDetails.subject_code,
            subject_name_short: subjectDetails.subject_name_short,
            subject_name: subjectDetails.subject_name,
            ac_year_id: subjectDetails.ac_year_id,
            ac_year: subjectDetails.ac_year,
            year: subjectDetails.year,
            sem: subjectDetails.sem,
        }
        setValue( 'subject', subjectValue )
        setValue( 'year', subjectDetails.year )
        setValue( 'sem', subjectDetails.sem )
        setValue( 'access_groups', [] )
    }

    const handleNewTopicAdd = ( event, module, values, setFieldValue, setFieldError ) => {

        if ( event.type === 'keydown' && event.key === "Enter" ) {
            event.preventDefault()
            if ( event.target.value.trim() ) {
                addTopic(
                    module,
                    event.target.value.trim(),
                    values,
                    setFieldValue
                )
                setFieldValue( `${module}-topicInput`, "" )
            } else {
                setFieldError( `${module}-topicInput`, "Can not add an empty value as topic." )
                // setFieldTouched( `${module}-topicInput`, true )
            }
            event.target.focus()
        } else if ( event.type === 'click' ) {
            if ( values[`${module}-topicInput`]?.trim() ) {
                addTopic(
                    module,
                    values[`${module}-topicInput`].trim(),
                    values,
                    setFieldValue
                )
                setFieldValue( `${module}-topicInput`, "" )
            } else {
                setFieldError( `${module}-topicInput`, "Can not add an empty value as topic." )
                // setFieldTouched( `${module}-topicInput`, true )
            }
        }

    }

    const handleAcademicYearChange = ( event, setFieldValue ) => {
        setFieldValue( 'academicYear', event.target.value )
        setFieldValue( 'subject', null )
        setFieldValue( 'year', '' )
        setFieldValue( 'sem', '' )
        setFieldValue( 'access_groups', [] )
    }

    const createPracticeSessions = async ( values, { resetForm } ) => {
        setCreating( true )
        const { success, data } = await SimbaAIStore.createPracticeSession( values )
        if ( success ) {
            resetForm()
            onClose()
            if ( data?._id )
                navigate( `/faculty/practice-with-simba/${data?._id}` )
        }
        setCreating( false )
    }

    const updatePracticeSession = async ( values, { resetForm } ) => {
        setCreating( true )
        await api.simbaAI.updatePracticeSession( practiceSession._id, values )
        onClose()
        setCreating( false )
        resetForm()
    }

    const getFacultySubjects = useCallback( async () => {
        setFetchingSubjects( true )
        if ( UserStore.getErrorsStatuses?.subjects )
            await UserStore.fetchUserSubjects()
        setFetchingSubjects( false )
        const subjects = ( UserStore.getUserSubjectsDetails?.currentSubjects || [] ).filter( sub => sub.ac_year_id === UserStore.getUserAcademicYears[0]?.ac_year_id )

        const years = Array.from( new Set( subjects.map( item => item.year ) ) )
        const sems = subjects.reduce( ( acc, item ) => {
            if ( !acc[item.year] ) {
                acc[item.year] = []
            }
            acc[item.year] = Array.from( new Set( [...acc[item.year], item.sem] ) )
            return acc
        }, {} )
        setYearsAndSems( {
            years: years,
            sems: sems
        } )
    }, [UserStore] )


    useEffect( () => {
        getFacultySubjects()
    }, [getFacultySubjects] )

    return (
        <Box >
            <Box onClick={event => setOpen( false )} display={open ? "block" : "none"} position="fixed" bgcolor={`rgba(222, 222, 222, 0.33)`} sx={{ inset: 0, zIndex: zIndex.drawer * 2 }}>
                <Box onClick={event => event.stopPropagation()} bgcolor={palette.background.paper} display="flex" flexDirection="column" maxHeight="90vh" width={{ sm: "95%", xs: "100%" }} maxWidth="1024px" borderRadius="10px" overflow="auto" boxShadow={shadows[1]} sx={{ zIndex: 300 }} position="absolute" bottom={{ sm: "20px", xs: "0px" }} right={{ sm: "20px", xs: "0px" }}>
                    <Box display="flex" flexDirection="column" flexGrow={1} position="relative" overflow="auto">
                        {/* <ReferenceMaterialUploadDialog attachments={uploadingAttachments} setAttachments={setUploadingAttachments} open={uploadingAttachments.length > 0} onClose={() => setUploadingAttachments( [] )} /> */}
                        <Box position="sticky" borderBottom={border[1]} top="0" bgcolor={palette.form.formCardBg} sx={{ zIndex: 5 }} display="flex" justifyContent="space-between" gap="20px" alignItems="center" padding="20px">
                            <Box>
                                <Typography variant="h1" fontWeight="600" component="h1" fontSize="16px">{mode === "edit" ? `Edit ${practiceSession.title}` : "Create new practice session"}</Typography>
                                {mode === "create" && <Typography variant="subtitle2" fontSize="12px" color='textSecondary' >Create new practice session by providing all required details</Typography>}
                                {mode === "edit" && <Typography variant="subtitle2" fontSize="12px" color='textSecondary' >Update the practice session title, reference materials and access groups.</Typography>}
                            </Box>
                            <IconButton onClick={() => onClose( false )} >{Icons.default.CloseIcon}</IconButton>
                        </Box>
                        <Box flexGrow={1} overflow="auto" bgcolor={palette.form.formCardBg} height="100%" padding={layout.pagePadding} >
                            <Formik onSubmit={mode === "edit" ? updatePracticeSession : createPracticeSessions} validationSchema={validationSchema} initialValues={initialValues} >
                                {( { values, setFieldValue, setFieldError, setFieldTouched, errors } ) => {
                                    console.log( errors )

                                    return (
                                        <Form>
                                            <FormControl id="createPracticeSessionFormControlForTitle" fullWidth>
                                                <Typography component="label" htmlFor='title' gutterBottom variant='subtitle2' color={palette.form.labelColor}>Name of the practice session *</Typography>
                                                <Field autoFocus as={TextField} size="small" onFocus={event => event.target.select()} id="createPracticeSessionFormControlForTitle" placeholder="Title; Provide a consise title to topics covered." type="text" name="title" />
                                                <FormHelperText><ErrorMessage name='title' /></FormHelperText>
                                            </FormControl>
                                            <FormControl id="createPracticeSessionFormControlForAcYear" fullWidth margin='normal'>
                                                <Box justifyContent="space-between" display="flex" gap="10px" alignItems="flex-end">
                                                    <Typography component="label" htmlFor='academicYearInput' gutterBottom variant='subtitle2' color={palette.form.labelColor}>
                                                        Select academic year *
                                                    </Typography>
                                                    <Button id="academicYearRefreshBtn" disabled={refreshing === "years"} startIcon={refreshing === "years" ? <CircularProgress size={14} /> : Icons.default.Sync} onClick={refreshYears} sx={{ width: "fit-content", flexShrink: 0, fontSize: "12px" }} color="secondary" >
                                                        {refreshing === "years" ? "Refreshing academic years..." : "Refresh academic years"}
                                                    </Button>
                                                </Box>
                                                <CustomSelectField itemValueKey={"ac_year_id"} renderOptionText={item => item.ac_year} data={UserStore.getUserAcademicYears} placeholder='Academic Year' size="small" onChange={event => handleAcademicYearChange( event, setFieldValue )} value={values.academicYear} />
                                            </FormControl>

                                            {<FormControl id="createPracticeSessionFormControlForSubject" margin='normal' fullWidth>
                                                <Typography component="label" gutterBottom variant='subtitle2' color={palette.form.labelColor}>Choose the subject based on academic year {UserStore.getUserAcademicYears.find( ac => ac.ac_year_id === values.academicYear )?.ac_year} *</Typography>
                                                <CustomSelectField
                                                    id="subjectSelector"
                                                    size="small"
                                                    renderValue={selected => {
                                                        if ( selected ) {
                                                            const subject = UserStore.getUserSubjectsDetails?.currentSubjects.find( sub => +sub.subject_id === +selected )
                                                            if ( subject )
                                                                return `${subject.subject_name?.toLowerCase()} (${subject.subject_code})`
                                                            else
                                                                return "Subject"
                                                        } else {
                                                            return "Subject"
                                                        }
                                                    }}
                                                    itemValueKey="subject_id"
                                                    itemIdentityKey="unique_subject_key"
                                                    data={( UserStore.getUserSubjectsDetails?.currentSubjects || [] ).filter( sub => subjectFilterFunc( sub, values ) )}
                                                    disabled={!values.academicYear}
                                                    sx={{ color: !values.subject && palette.form.placeholder, textTransform: "capitalize" }}
                                                    value={fetchingSubjects ? "" : values?.subject?.subject_id || ""}
                                                    onChange={e => handleSubjectChange( e, setFieldValue, values.start_date )}
                                                    name="subject"
                                                    renderOptionText={( item, index ) => <Box id={`subject-option-${index}`} display="flex" flexDirection="column">
                                                        <Typography textTransform="capitalize" variant='subtitle2'>
                                                            {item.subject_name?.toLowerCase()}
                                                        </Typography>
                                                        <Box display="flex" gap="5px" alignItems="center">
                                                            <CustomChip tooltipTitle='Subject code' value={item.subject_code} />
                                                            <CustomChip tooltipTitle='Year' value={`Year: ${item.year}`} />
                                                            {item.sem && <CustomChip tooltipTitle='Semester' value={`Sem: ${item.sem}`} />}
                                                        </Box>
                                                    </Box>}
                                                />
                                                {UserStore.getErrorsStatuses?.subjects && <Box bgcolor={palette.errorMessage.light + "22"} marginTop="10px" borderRadius="5px" padding="5px 10px" border={`1px dotted ${palette.errorMessage.main}`} display="flex" alignItems="center" gap="5px" fontSize="12px" color="textSecondary">
                                                    <Typography color="errorMessage.main" variant='subtitle2'>Failed to fetch <strong><em>subjects</em></strong> from ERP...</Typography>
                                                    <Button color="errorMessage" sx={{ textTransform: "capitalize" }} onClick={() => getFacultySubjects()} endIcon={fetchingSubjects ? <CircularProgress size={14} /> : Icons.default.Replay}>Retry</Button>
                                                </Box>}
                                                {fetchingSubjects && !UserStore.getErrorsStatuses?.subjects && <DataLoadingSpinner waitingMessage={`Fetching your subjects from ERP...`} padding='0' size={14} />}
                                                <FormHelperText> <ErrorMessage name="subject" /> </FormHelperText>
                                            </FormControl>}

                                            {values.subject && <FormControl id="createPracticeSessionFormControlForStudentGroups" margin='normal' fullWidth>
                                                <Box justifyContent="space-between" display="flex" gap="10px" alignItems="flex-end">
                                                    <Typography display="block" variant="subtitle2" color={palette.form.labelColor} gutterBottom >
                                                        Select single or multiple batchs/sections for students to practice. *
                                                    </Typography>
                                                    <Button disabled={refreshing === "batches" || !values.academicYear} startIcon={refreshing === "batches" ? <CircularProgress size={14} /> : !values.academicYear ? Icons.default.ArrowBack : Icons.default.Sync} onClick={() => refreshBatches( values.academicYear )} sx={{ width: "fit-content", flexShrink: 0, marginTop: "2px", fontSize: "12px" }} color="secondary" >
                                                        {refreshing === "batches" ? "Refreshing batches..." : !values.academicYear ? "Please select a academic year." : "Refresh batches"}
                                                    </Button>
                                                </Box>

                                                <CustomSelectField
                                                    onChange={event => handleAccessGroupChange( event, setFieldValue )}
                                                    options={{ showCheckbox: true }}
                                                    slotProps={{
                                                        input: {
                                                            sx: {
                                                                padding: values.access_groups?.length > 0 ? "5px" : ""
                                                            }
                                                        }
                                                    }}
                                                    searchFilter={( item, searchTerm ) => {
                                                        return item.short_title?.toLowerCase()?.includes( searchTerm?.toLowerCase() )
                                                    }}
                                                    renderValue={value => {
                                                        if ( value?.length === 0 ) {
                                                            return "Select batches and sections."
                                                        } else {
                                                            return <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: "5px" }}>
                                                                {values.access_groups.map( ( ag, index ) => (
                                                                    <Box padding="2px 5px" bgcolor={palette.contentBg} border={border[1]} borderRadius="10px" key={ag.id} display="flex" alignItems="center">
                                                                        <Typography fontSize="12px" variant='subtitle2'>
                                                                            {ag.chip_label}
                                                                        </Typography>
                                                                        <IconButton onMouseDown={e => e.stopPropagation()} onClick={event => removeSelectedAccessGroup( event, index, values, setFieldValue )} size='small' color='error'>
                                                                            {Icons.default.CancelIcon}
                                                                        </IconButton>
                                                                        {/* <Typography color='textSecondary' fontSize="12px" component="span">
                                                                            {ag.academic_details}
                                                                        </Typography> */}
                                                                    </Box>
                                                                ) )}
                                                            </Box>
                                                        }
                                                    }}
                                                    size="small"
                                                    value={values.selected_access_groups || ""}
                                                    data={{
                                                        sections: UserStore.getUsersAllBatchesAndSectionList?.filter( item => accessGroupsFilterFunc( item, values, "section" ) ),
                                                        batches: UserStore.getUsersAllBatchesAndSectionList?.filter( item => accessGroupsFilterFunc( item, values, "batch" ) )
                                                    }}
                                                    id="batchesAndSectionsInput"
                                                    itemValueKey="id"
                                                    multiple
                                                    renderOptionText={item => <Box display="flex" flexDirection="column">
                                                        <Typography variant='subtitle2'>
                                                            {item.short_title}
                                                        </Typography>
                                                        <Box display="flex" gap="5px" alignItems="center">
                                                            <CustomChip tooltipTitle='Course short name' value={item.course_name_short} />
                                                            <CustomChip tooltipTitle='Year' value={`Year: ${item.current_year}`} />
                                                            {item.current_year && <CustomChip tooltipTitle='Semester' value={`Sem: ${item.current_sem}`} />}
                                                        </Box>
                                                    </Box>}
                                                />
                                                <FormHelperText><ErrorMessage name='access_groups' /></FormHelperText>
                                            </FormControl>}


                                            {values.subject && <FormControl margin="normal" fullWidth>
                                                <Typography
                                                    gutterBottom
                                                    variant="subtitle2"
                                                    color={palette.form.labelColor}
                                                >
                                                    Add modules and topics for the practice *
                                                </Typography>
                                                {Object.keys( values.modules_and_topics ).length === 0 && <AddModuleButton onselect={( mod ) => handleNewModuleSelect( mod, values, setFieldValue )} modules={moduleOptions.filter( mod => !Object.keys( values.modules_and_topics ).includes( mod ) )} />}

                                                {/* Initial */}
                                                {/* {Object.keys( values.modules_and_topics ).length === 0 && <Box bgcolor={palette.contentBg} gap={layout.cardsGap} padding="20px" borderRadius={layout.borderRadius} border={`2px dashed ${palette.borderColor}`} display="flex" flexDirection="column" alignItems="center">
                                                    <Typography textAlign="center" variant='subtitle2' color='textSecondary' maxWidth="350px">No modules added yet. Click below to add your first module and start building the practice syllabus.</Typography>
                                                    <AddModuleButton onselect={( mod ) => handleNewModuleSelect( mod, values, setFieldValue )} modules={moduleOptions.filter( mod => !Object.keys( values.modules_and_topics ).includes( mod ) )} />
                                                </Box>} */}

                                                {/* MODULE DETAILS */}
                                                {Object.keys( values.modules_and_topics ).length > 0 && <Box display="flex" flexDirection="column" gap="10px">
                                                    {Object.keys( values.modules_and_topics ).map( module => {
                                                        const moduleDetails = values.modules_and_topics[module]
                                                        return (
                                                            <Box padding="10px" key={module} border={border[1]} overflow="clip" borderRadius={layout.borderRadius} display="flex" flexDirection="column" gap={layout.cardsGap}>
                                                                {/* MODULE HEADER */}
                                                                <Box display="flex" gap={layout.cardsGap}   >
                                                                    <Typography width="fit-content" bgcolor={palette.customThemeColor.main} display="flex" alignItems="center" fontSize="12px" padding="2px 5px" borderRadius={layout.borderRadius} color={palette.customThemeColor.contrastText} variant="subtitle2">
                                                                        {module.toUpperCase()}
                                                                    </Typography>
                                                                    <Button
                                                                        variant='outlined'
                                                                        size="small"
                                                                        color="error"
                                                                        onClick={() =>
                                                                            removeModule( module, values, setFieldValue )
                                                                        }
                                                                        sx={{ flexShrink: 0 }}
                                                                        startIcon={Icons.default.CloseIcon}
                                                                    >
                                                                        Remove {module}
                                                                    </Button>
                                                                </Box>
                                                                <Box id="createPracticeSessionFormControlForModuleName">
                                                                    <Typography component="label" htmlFor='moduleNameInput' display="block" gutterBottom variant='subtitle2' color={palette.form.labelColor}>Module Name *</Typography>
                                                                    <TextField
                                                                        id="moduleNameInput"
                                                                        fullWidth
                                                                        autoFocus
                                                                        onFocus={event => event.target.select()}
                                                                        size='small'
                                                                        value={moduleDetails.name}
                                                                        onChange={( e ) => updateModuleName( module, e.target.value, values, setFieldValue )}
                                                                        placeholder={`Enter ${module} name...`}
                                                                    />
                                                                </Box>
                                                                {/* ADD TOPIC */}
                                                                <Box id="createPracticeSessionFormControlForModuleTopics">
                                                                    <Typography component="label" htmlFor='topicsInput' display="block" gutterBottom variant='subtitle2' color={palette.form.labelColor}>List of topics to practice *</Typography>
                                                                    <Box display="flex" flexWrap="wrap" gap="5px">
                                                                        <Field as={TextField}
                                                                            name={`${module}-topicInput`}
                                                                            id="topicsInput"
                                                                            placeholder='Type topics and press enter or click on add button to add.'
                                                                            size="small"
                                                                            value={values[`${module}-topicInput`] || ""}
                                                                            onChange={event => setFieldValue( `${module}-topicInput`, event.target.value )}
                                                                            onKeyDown={event => handleNewTopicAdd( event, module, values, setFieldValue, setFieldError, setFieldTouched )}
                                                                            sx={{
                                                                                flexGrow: 1
                                                                            }}
                                                                        />

                                                                        <Button
                                                                            sx={{ width: "max-content", flexShrink: 0, display: "flex" }}
                                                                            startIcon={Icons.default.AddIcon}
                                                                            variant="contained"
                                                                            disableElevation
                                                                            onClick={( event ) => handleNewTopicAdd( event, module, values, setFieldValue, setFieldError, setFieldTouched )}
                                                                        >
                                                                            Add Topic
                                                                        </Button>
                                                                    </Box>

                                                                    <FormHelperText>
                                                                        <ErrorMessage name={`${module}-topicInput`} />
                                                                    </FormHelperText>

                                                                    {/* TOPIC TAGS */}
                                                                    {moduleDetails.topics.length > 0 && <Box marginTop="10px" display="flex" flexWrap="wrap" gap="5px">
                                                                        {moduleDetails.topics.map(
                                                                            ( topic, index ) => (
                                                                                <Box
                                                                                    key={topic}
                                                                                    border={`1px solid ${palette.common.font}`}
                                                                                    bgcolor={palette.contentBg}
                                                                                    borderRadius={layout.borderRadius}
                                                                                    px="6px"
                                                                                    py="2px"
                                                                                    display="flex"
                                                                                    alignItems="center"
                                                                                    gap="5px"
                                                                                >
                                                                                    <Typography fontSize="10px" width="20px" sx={{ aspectRatio: "1 / 1" }} display="flex" alignItems="center" justifyContent="center" borderRadius="50%" border={`1px solid ${palette.common.font}`} >{index + 1}</Typography>
                                                                                    <Typography marginRight="10px" fontSize="12px">{topic}</Typography>
                                                                                    <IconButton
                                                                                        color='error'
                                                                                        size="small"
                                                                                        onClick={() =>
                                                                                            removeTopic(
                                                                                                module,
                                                                                                index,
                                                                                                values,
                                                                                                setFieldValue
                                                                                            )
                                                                                        }
                                                                                    >
                                                                                        {Icons.default.CloseIcon}
                                                                                    </IconButton>
                                                                                </Box>
                                                                            )
                                                                        )}
                                                                    </Box>}
                                                                </Box>
                                                            </Box>
                                                        )
                                                    } )}
                                                    {/* <AddModuleButton label="Add another module" onselect={( mod ) => handleNewModuleSelect( mod, values, setFieldValue )} modules={moduleOptions.filter( mod => !Object.keys( values.modules_and_topics ).includes( mod ) )} /> */}
                                                </Box>}
                                                <FormHelperText>
                                                    <ErrorMessage name="modules_and_topics" />
                                                </FormHelperText>
                                            </FormControl>}

                                            {values.subject && <FormControl id="createPracticeSessionFormControlForComplexity" fullWidth>
                                                <Typography component="label" htmlFor='title' gutterBottom variant='subtitle2' color={palette.form.labelColor}>Choose quiz difficulty level *</Typography>
                                                <Field
                                                    as={CustomSelectField}
                                                    size="small"
                                                    id="createPracticeSessionFormControlForComplexity"
                                                    placeholder="Select difficulty level"
                                                    data={complexityOptions}
                                                    onChange={event => setFieldValue( 'complexity', event.target.value )}
                                                    value={values.complexity}
                                                    renderOptionText={item => item.label}
                                                    itemValueKey="value"
                                                />
                                            </FormControl>}

                                            {mode !== "edit" && values.subject && <Box
                                                marginTop="20px"
                                                id="createPracticeSessionFormControlForDateRange"
                                            >
                                                <Typography variant="subtitle2" color={palette.form.labelColor} gutterBottom >Select date range in which these topics were taught to the class*</Typography>
                                                <Box display="flex" flexWrap="wrap" gap="10px" alignItems="center">
                                                    <FormControl sx={{ minWidth: "170px", width: { sm: "250px", xs: "auto" }, "& .MuiPickersSectionList-root": { width: { sm: "182px", xs: "auto" } } }}>
                                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                            <DatePicker
                                                                {...( components?.MuiPickerOutlinedInput?.defaultProps || {} )}
                                                                sx={{ width: "100%", fontSize: "12px", '& input': { padding: "10px" } }}
                                                                disableToolbar
                                                                id="start-date"
                                                                variant="inline"
                                                                format="DD-MM-YYYY"
                                                                value={dayjs( values.date_range_start )}
                                                                onChange={val => {
                                                                    if ( val && val.isValid() )
                                                                        setFieldValue( 'date_range_start', val )
                                                                }}
                                                                KeyboardButtonProps={{
                                                                    'aria-label': 'change date',
                                                                }}
                                                            />
                                                        </LocalizationProvider>
                                                    </FormControl>
                                                    <Typography variant='subtitle2' color='textSecondary'>to</Typography>
                                                    <FormControl sx={{ minWidth: "170px", width: { sm: "250px", xs: "auto" }, "& .MuiPickersSectionList-root": { width: { sm: "182px", xs: "auto" } } }}>
                                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                            <DatePicker
                                                                {...( components?.MuiPickerOutlinedInput?.defaultProps || {} )}
                                                                sx={{ width: "100%", fontSize: "12px", '& input': { padding: "10px" } }}
                                                                disableToolbar
                                                                id="start-date"
                                                                variant="inline"
                                                                format="DD-MM-YYYY"
                                                                maxDate={dayjs()}
                                                                value={dayjs( values.date_range_end )}
                                                                onChange={val => {
                                                                    if ( val && val.isValid() )
                                                                        setFieldValue( 'date_range_end', val )
                                                                }}
                                                                KeyboardButtonProps={{
                                                                    'aria-label': 'change date',
                                                                }}
                                                            />
                                                        </LocalizationProvider>
                                                    </FormControl>
                                                </Box>
                                                <FormHelperText>
                                                    <ErrorMessage name="date_range_start" />
                                                </FormHelperText>
                                            </Box>
                                            }

                                            {values.subject && <FormControl id="createPracticeSessionFormControlForCloseDate" margin='normal'>
                                                <Typography variant="subtitle2" color={palette.form.labelColor} gutterBottom >Specify a deadline to restrict practice after a certain date. (optional)</Typography>
                                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                    <DatePicker
                                                        {...( components?.MuiPickerOutlinedInput?.defaultProps || {} )}
                                                        sx={{ width: "100%", fontSize: "12px", '& input': { padding: "10px" } }}
                                                        disableToolbar
                                                        id="close-date"
                                                        variant="inline"
                                                        format="DD-MM-YYYY"
                                                        minDate={dayjs().add( 5, 'days' )}
                                                        value={dayjs( values.close_date ).subtract( 1, 'day' )}
                                                        onChange={val => {
                                                            if ( val && val.isValid() )
                                                                setFieldValue( 'close_date', dayjs( val ).add( 1, 'day' ) )
                                                        }}
                                                        KeyboardButtonProps={{
                                                            'aria-label': 'change date',
                                                        }}
                                                    />
                                                </LocalizationProvider>
                                                <FormHelperText>
                                                    <ErrorMessage name="close_date" />
                                                </FormHelperText>
                                            </FormControl>}

                                            <Box justifyContent="flex-end" display="flex" marginTop="40px" gap="10px">
                                                <Button variant='outlined' disabled={creating} onClick={() => onClose( false )} color="error" startIcon={Icons.default.CloseIcon} sx={{ width: "max-content", textTransform: "capitalize" }} >Cancel</Button>
                                                <Button id="practiceSessionCreateSubmitBtn" disabled={creating} type='submit' variant='contained' disableElevation color="primary" startIcon={creating ? <CircularProgress size={20} /> : mode === 'edit' ? Icons.default.CheckIcon : Icons.default.AddIcon} sx={{ width: "max-content", textTransform: "capitalize" }} >{mode === "edit" ? "Save changes" : "Create Practice session"}</Button>
                                            </Box>
                                        </Form>
                                    )
                                }}
                            </Formik>
                        </Box>
                    </Box>
                </Box >
            </Box>
        </Box >
    )
} )

const CreateOrEditPracticeSessionDialog = ( { variant = "fab", buttonLabel = "Create new practice session", ...rest } ) => {

    const [open, setOpen] = useState( false )

    const { palette, layout, breakpoints } = useTheme()

    const sm = useMediaQuery( breakpoints.up( 'sm' ) )

    return (
        <Box>

            {open && <CreateForm mode="create" open={open} setOpen={setOpen} />}

            {variant === "fab" && <Box
                padding="5px"
                borderRadius="40px"
                overflow={"visible"}
                bottom={sm ? layout.pagePadding : "70px"}
                sx={{
                    position: "fixed",
                    right: layout.pagePadding,
                    zIndex: 100,
                    "&:hover .simba-img": {
                        transform: " scale(1.1)",
                        transition: "200ms ease"
                    }
                }}>

                {/* <Box position="absolute" className="animation-transform-bg" sx={{ inset: 0 }} /> */}
                <Fab
                    id='fab-for-create'
                    color='error'
                    variant='extended'
                    sx={{
                        padding: "30px 20px",
                        zIndex: 10,
                        gap: "10px",
                        textTransform: "capitalize",
                        boxShadow: `0px 0px 20px 1px ${palette.error.main}`
                    }} onClick={() => setOpen( true )}>
                    {Icons.default.AddIcon}
                    {buttonLabel}
                </Fab>
                <Box position="absolute" src={buttonWaveSimba} className='simba-img' component="img" height="80px" sx={{ zIndex: 12, pointerEvents: "none", transform: "scale(1)", transformOrigin: "bottom center", transition: "200ms ease" }} bottom="calc(100% - 25px)" right="0" />
            </Box>}

            {variant === "button" && <Button variant='contained' {...rest} onClick={() => setOpen( true )} >{buttonLabel}</Button>}
        </Box>
    )
}

export default CreateOrEditPracticeSessionDialog


/*


const toggleRefMaterialSelection = ( id, index, values, setFieldValue ) => {
        try {
            const newList = [...values.reference_materials]
            if ( index >= 0 ) {
                newList.splice( index, 1 )
            } else {
                newList.push( id )
            }
            setFieldValue( 'reference_materials', newList )
        } catch ( err ) {
            console.log( err )
        }
    }
<FormControl fullWidth margin='normal'>
                                        <Typography variant="subtitle2" color={palette.form.labelColor} gutterBottom >Select reference materials to create questions from *</Typography>
                                        <Box display="flex" gap="5px" flexWrap="wrap">
                                            {!SimbaAIStore.getErrors.referenceMaterials && SimbaAIStore.getReferenceMaterials.filter( ps => ps?.subject?.subject_code === subject?.subject_code ).map( refMaterial => {
                                                const refMaterialIndex = values.reference_materials.findIndex( rm => rm === refMaterial._id )
                                                const selected = refMaterialIndex >= 0
                                                return (
                                                    <Box onClick={() => toggleRefMaterialSelection( refMaterial._id, refMaterialIndex, values, setFieldValue )} key={refMaterial._id} sx={{ cursor: "pointer", ":hover": { bgcolor: !selected && palette.rowHover } }} display="flex" gap="10px" alignItems="center" fontSize="14px" bgcolor={!selected ? palette.contentBg : palette.primary.light + "22"} padding="5px 10px" border={selected ? `1px solid ${palette.primary.main}` : border[1]} borderRadius={layout.borderRadius} >
                                                        {selected ? Icons.default.CheckboxChecked : Icons.default.Checkbox}
                                                        <Typography fontSize="12px" color={selected ? "textPrimary" : "textSecondary"} variant='subtitle2'>{refMaterial.name}</Typography>
                                                    </Box>
                                                )
                                            } )}

                                            {!SimbaAIStore.getErrors.referenceMaterials && SimbaAIStore.getReferenceMaterials.filter( ps => ps?.subject?.subject_code === subject?.subject_code ).length > 0 && <Button startIcon={Icons.default.CloudUpload} onClick={() => refUploadInputRef.current?.click()} variant='outlined' size='small' color='greyed'>
                                                Upload more Reference materials
                                            </Button>}

                                            {!SimbaAIStore.getErrors.referenceMaterials && SimbaAIStore.getReferenceMaterials.filter( ps => ps?.subject?.subject_code === subject?.subject_code ).length === 0 && <Box padding="10px" borderRadius={layout.borderRadius} width="100%" border={border[1]} display="flex" flexDirection="column" gap="10px" alignItems="center" >
                                                <Typography variant='subtitle2' color='textSecondary' >No reference materials found for the selected subjects.</Typography>
                                                <Button startIcon={Icons.default.CloudUpload} onClick={() => refUploadInputRef.current?.click()} variant='outlined' size='small' color='greyed'>
                                                    Upload Reference materials
                                                </Button>
                                            </Box>}
                                            {SimbaAIStore.getErrors.referenceMaterials && <Box padding="10px" borderRadius={layout.borderRadius} width="100%" border={border[1]} display="flex" flexDirection="column" gap="10px" alignItems="center" >
                                                <Typography variant='subtitle2' color='textSecondary' >Failed to load subject reference materials please retry.</Typography>
                                                <Button onClick={getReferenceMaterials} startIcon={Icons.default.Sync} variant='outlined' size='small' color='error'>
                                                    Retry
                                                </Button>
                                            </Box>}
                                        </Box>
                                    </FormControl>

*/